import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import {
    Button,
    Input,
    InputNumber,
    Modal,
    Popconfirm,
    Radio,
    Select,
    Space,
    Spin,
    Table,
    Tag,
    message,
} from "antd";
import { EditOutlined, PlusOutlined, StopOutlined } from "@ant-design/icons";
import { ResumeDetailModal } from "../../components/ResumeDetail";
import { JobManagementPageModel } from "../pageModels/JobManagementPageModel";
import {
    RemoteJob,
    JOB_TYPE_OPTIONS,
    formatSalaryRange,
    getJobStatusText,
    getJobTypeText,
    isJobActive,
} from "../../models/RemoteJob";
import {
    JobApplicant,
    ApplicationManageStatus,
    getApplicationStatusText,
} from "../../models/JobApplicant";
import {
    calculateWorkYears,
    getEducationText,
    getGenderText,
} from "../../models/Resume";
import { globalStore } from "../../models/globalStore";
import "../styles/JobManagement.css";

const { TextArea } = Input;

const formatDateTime = (time: Date | string | undefined): string => {
    if (!time) {
        return "-";
    }
    return time instanceof Date ? time.toLocaleString() : new Date(time).toLocaleString();
};

const JobManagement: React.FC = observer(() => {
    const [pageModel] = useState(() => new JobManagementPageModel());

    useEffect(() => {
        globalStore.selectedMenuKey = "job-management";
        pageModel.loadJobs();
    }, [pageModel]);

    const handleSave = async () => {
        const { title, description, salaryRangeStart, salaryRangeEnd } = pageModel.editingJob;
        if (!title.trim()) {
            message.error("请输入岗位标题");
            return;
        }
        if (!description.trim()) {
            message.error("请输入岗位描述");
            return;
        }
        if (salaryRangeStart <= 0 || salaryRangeEnd <= 0) {
            message.error("请输入有效的薪资范围");
            return;
        }
        if (salaryRangeStart > salaryRangeEnd) {
            message.error("薪资下限不能大于上限");
            return;
        }

        const success = await pageModel.saveJob();
        if (success) {
            message.success(pageModel.isEditMode ? "修改成功" : "发布成功");
        } else {
            message.error(pageModel.isEditMode ? "修改失败" : "发布失败");
        }
    };

    const handleClose = async (job: RemoteJob) => {
        const success = await pageModel.closeJob(job.id);
        if (success) {
            message.success("下架成功");
        } else {
            message.error("下架失败");
        }
    };

    const handleUpdateApplicationStatus = async (status: ApplicationManageStatus) => {
        const success = await pageModel.updateApplicationStatus(status);
        if (success) {
            message.success("状态更新成功");
        } else {
            message.error("状态更新失败");
        }
    };

    const renderJobSummary = (job: RemoteJob) => (
        <>
            <div className="job-detail-title">{job.title}</div>
            <div className="job-detail-meta">
                <Tag color="blue">{getJobTypeText(job.type)}</Tag>
                <Tag color={isJobActive(job) ? "success" : "default"}>
                    {getJobStatusText(job)}
                </Tag>
            </div>
            <div className="job-detail-item">
                <span className="job-detail-label">薪资范围</span>
                <span>{formatSalaryRange(job.salaryRangeStart, job.salaryRangeEnd)}</span>
            </div>
            <div className="job-detail-item">
                <span className="job-detail-label">发布时间</span>
                <span>{formatDateTime(job.createdAt)}</span>
            </div>
            <div className="job-detail-section-title">岗位描述</div>
            <div className="job-detail-description">{job.description}</div>
        </>
    );

    const applicantColumns = [
        {
            title: "姓名",
            dataIndex: "name",
            key: "name",
            width: 90,
            render: (name: string) => name || "-",
        },
        {
            title: "性别",
            key: "gender",
            width: 70,
            render: (_: unknown, record: JobApplicant) =>
                record.gender != null ? getGenderText(record.gender) : "-",
        },
        {
            title: "学历",
            key: "education",
            width: 100,
            render: (_: unknown, record: JobApplicant) =>
                record.education != null ? getEducationText(record.education) : "-",
        },
        {
            title: "年限",
            key: "workYears",
            width: 90,
            render: (_: unknown, record: JobApplicant) =>
                record.firstWorkDate ? `${calculateWorkYears(record.firstWorkDate)}年` : "-",
        },
        {
            title: "手机",
            dataIndex: "phone",
            key: "phone",
            width: 120,
            render: (phone: string) => phone || "-",
        },
        {
            title: "投递时间",
            key: "appliedAt",
            width: 160,
            render: (_: unknown, record: JobApplicant) => formatDateTime(record.appliedAt),
        },
        {
            title: "状态",
            key: "status",
            width: 90,
            render: (_: unknown, record: JobApplicant) => getApplicationStatusText(record.status),
        },
    ];

    const columns = [
        {
            title: "岗位标题",
            dataIndex: "title",
            key: "title",
            width: 180,
        },
        {
            title: "类型",
            dataIndex: "type",
            key: "type",
            width: 130,
            render: (type: number) => getJobTypeText(type),
        },
        {
            title: "薪资范围",
            key: "salary",
            width: 120,
            render: (_: unknown, record: RemoteJob) =>
                formatSalaryRange(record.salaryRangeStart, record.salaryRangeEnd),
        },
        {
            title: "状态",
            key: "status",
            width: 90,
            render: (_: unknown, record: RemoteJob) => (
                <Tag color={isJobActive(record) ? "success" : "default"}>
                    {getJobStatusText(record)}
                </Tag>
            ),
        },
        {
            title: "发布时间",
            dataIndex: "createdAt",
            key: "createdAt",
            width: 160,
            render: (time: Date | string) => formatDateTime(time),
        },
        {
            title: "更新时间",
            dataIndex: "updatedAt",
            key: "updatedAt",
            width: 160,
            render: (time: Date | string) => formatDateTime(time),
        },
        {
            title: "操作",
            key: "action",
            width: 160,
            render: (_: unknown, record: RemoteJob) => (
                <Space onClick={(e) => e.stopPropagation()}>
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => pageModel.showEditModal(record)}
                    >
                        编辑
                    </Button>
                    {isJobActive(record) && (
                        <Popconfirm
                            title="确认下架该岗位？"
                            description="下架后卖家端将不再展示此岗位"
                            onConfirm={() => handleClose(record)}
                            okText="确认"
                            cancelText="取消"
                        >
                            <Button
                                type="link"
                                danger
                                icon={<StopOutlined />}
                                loading={pageModel.closingJobId === record.id}
                            >
                                下架
                            </Button>
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ];

    const selectedJob = pageModel.selectedJob;

    return (
        <div className="job-management-container">
            <div className="job-management-toolbar">
                <Radio.Group
                    value={pageModel.selectedStatus}
                    onChange={(e) => pageModel.setSelectedStatus(e.target.value)}
                >
                    <Radio.Button value="all">全部</Radio.Button>
                    <Radio.Button value="active">招聘中</Radio.Button>
                    <Radio.Button value="closed">已下架</Radio.Button>
                </Radio.Group>
                <Button type="primary" icon={<PlusOutlined />} onClick={pageModel.showCreateModal}>
                    发布岗位
                </Button>
            </div>

            <div className="job-management-list">
                <Table
                    rowKey="id"
                    columns={columns}
                    dataSource={pageModel.displayJobs}
                    loading={pageModel.loading}
                    pagination={{ pageSize: 20 }}
                    scroll={{ x: 1100 }}
                    onRow={(record) => ({
                        onClick: () => pageModel.showJobDetail(record),
                        style: { cursor: "pointer" },
                    })}
                />
            </div>

            <Modal
                title="岗位详情"
                open={pageModel.jobDetailModalVisible}
                onCancel={pageModel.closeJobDetailModal}
                footer={null}
                width={1000}
                destroyOnClose
                className="job-detail-modal"
            >
                {selectedJob && (
                    <div className="job-detail-modal-body">
                        <div className="job-detail-panel">
                            <div className="job-detail-title">{selectedJob.title}</div>
                            <div className="job-detail-meta">
                                <Tag color="blue">{getJobTypeText(selectedJob.type)}</Tag>
                                <Tag color={isJobActive(selectedJob) ? "success" : "default"}>
                                    {getJobStatusText(selectedJob)}
                                </Tag>
                            </div>
                            <div className="job-detail-item">
                                <span className="job-detail-label">薪资范围</span>
                                <span>
                                    {formatSalaryRange(
                                        selectedJob.salaryRangeStart,
                                        selectedJob.salaryRangeEnd,
                                    )}
                                </span>
                            </div>
                            <div className="job-detail-item">
                                <span className="job-detail-label">发布时间</span>
                                <span>{formatDateTime(selectedJob.createdAt)}</span>
                            </div>
                            <div className="job-detail-item">
                                <span className="job-detail-label">更新时间</span>
                                <span>{formatDateTime(selectedJob.updatedAt)}</span>
                            </div>
                            <div className="job-detail-section-title">岗位描述</div>
                            <div className="job-detail-description">{selectedJob.description}</div>
                        </div>
                        <div className="job-applicants-area">
                            <div className="job-detail-section-title">投递列表</div>
                            <Table
                                rowKey="applicationId"
                                size="small"
                                columns={applicantColumns}
                                dataSource={pageModel.applicants}
                                loading={pageModel.loadingApplicants}
                                pagination={{
                                    pageSize: 10,
                                    hideOnSinglePage: true,
                                    showSizeChanger: false,
                                }}
                                locale={{ emptyText: "暂无投递" }}
                                onRow={(record) => ({
                                    onClick: () => pageModel.showApplicationDetail(record),
                                    style: { cursor: "pointer" },
                                })}
                            />
                        </div>
                    </div>
                )}
            </Modal>

            <Modal
                title="投递详情"
                open={pageModel.applicationDetailVisible}
                onCancel={pageModel.closeApplicationDetailModal}
                footer={null}
                width={1000}
                destroyOnClose
                className="application-detail-modal"
                style={{ top: 20 }}
                bodyStyle={{ maxHeight: "90vh", overflow: "hidden" }}
            >
                {pageModel.selectedApplicant && pageModel.selectedJob && (
                    <div className="application-detail-body">
                        <div className="application-detail-resume">
                            {pageModel.loadingApplicationDetail ? (
                                <div className="application-detail-loading">
                                    <Spin />
                                </div>
                            ) : pageModel.applicationDetailResume ? (
                                <ResumeDetailModal resume={pageModel.applicationDetailResume} />
                            ) : (
                                <div className="application-detail-loading">暂无简历数据</div>
                            )}
                        </div>
                        <div className="application-detail-side">
                            <div className="application-detail-actions">
                                <Button
                                    block
                                    loading={pageModel.updatingApplicationStatus}
                                    disabled={
                                        pageModel.selectedApplicant.status === "reviewing" ||
                                        pageModel.selectedApplicant.status === "withdrawn"
                                    }
                                    onClick={() => handleUpdateApplicationStatus("reviewing")}
                                >
                                    标记审核
                                </Button>
                                <Popconfirm
                                    title="确认标记录用？"
                                    description="录用后将自动拒绝该岗位的其他投递"
                                    onConfirm={() => handleUpdateApplicationStatus("accepted")}
                                    okText="确认"
                                    cancelText="取消"
                                    disabled={pageModel.selectedApplicant.status === "withdrawn"}
                                >
                                    <Button
                                        block
                                        type="primary"
                                        loading={pageModel.updatingApplicationStatus}
                                        disabled={
                                            pageModel.selectedApplicant.status === "accepted" ||
                                            pageModel.selectedApplicant.status === "withdrawn"
                                        }
                                    >
                                        标记录用
                                    </Button>
                                </Popconfirm>
                                <Popconfirm
                                    title="确认标记拒绝？"
                                    onConfirm={() => handleUpdateApplicationStatus("rejected")}
                                    okText="确认"
                                    cancelText="取消"
                                    disabled={pageModel.selectedApplicant.status === "withdrawn"}
                                >
                                    <Button
                                        block
                                        danger
                                        loading={pageModel.updatingApplicationStatus}
                                        disabled={
                                            pageModel.selectedApplicant.status === "rejected" ||
                                            pageModel.selectedApplicant.status === "withdrawn"
                                        }
                                    >
                                        标记拒绝
                                    </Button>
                                </Popconfirm>
                            </div>
                            <div className="job-detail-section-title">投递信息</div>
                            <div className="job-detail-item">
                                <span className="job-detail-label">姓名</span>
                                <span>{pageModel.selectedApplicant.name || "-"}</span>
                            </div>
                            <div className="job-detail-item">
                                <span className="job-detail-label">投递状态</span>
                                <span>
                                    {getApplicationStatusText(pageModel.selectedApplicant.status)}
                                </span>
                            </div>
                            <div className="job-detail-item">
                                <span className="job-detail-label">投递时间</span>
                                <span>{formatDateTime(pageModel.selectedApplicant.appliedAt)}</span>
                            </div>

                            <div className="job-detail-section-title">岗位信息</div>
                            {renderJobSummary(pageModel.selectedJob)}
                        </div>
                    </div>
                )}
            </Modal>

            <Modal
                title={pageModel.isEditMode ? "修改岗位" : "发布岗位"}
                open={pageModel.showModal}
                onCancel={pageModel.closeModal}
                onOk={handleSave}
                confirmLoading={pageModel.saving}
                okText="保存"
                cancelText="取消"
                width={640}
                destroyOnClose
            >
                <div className="job-form-row">
                    <label className="job-form-label">岗位标题</label>
                    <Input
                        value={pageModel.editingJob.title}
                        placeholder="请输入岗位标题"
                        maxLength={128}
                        onChange={(e) => pageModel.updateEditingField("title", e.target.value)}
                    />
                </div>
                <div className="job-form-row">
                    <label className="job-form-label">岗位类型</label>
                    <Select
                        style={{ width: "100%" }}
                        value={pageModel.editingJob.type}
                        options={JOB_TYPE_OPTIONS}
                        onChange={(value) => pageModel.updateEditingField("type", value)}
                    />
                </div>
                <div className="job-form-row">
                    <label className="job-form-label">薪资范围</label>
                    <div className="job-salary-row">
                        <InputNumber
                            min={1}
                            max={999}
                            value={pageModel.editingJob.salaryRangeStart}
                            onChange={(value) =>
                                pageModel.updateEditingField("salaryRangeStart", value ?? 0)
                            }
                        />
                        <span className="job-salary-separator">-</span>
                        <InputNumber
                            min={1}
                            max={999}
                            value={pageModel.editingJob.salaryRangeEnd}
                            onChange={(value) =>
                                pageModel.updateEditingField("salaryRangeEnd", value ?? 0)
                            }
                        />
                        <span className="job-salary-unit">K/月</span>
                    </div>
                </div>
                <div className="job-form-row">
                    <label className="job-form-label">岗位描述</label>
                    <TextArea
                        rows={6}
                        value={pageModel.editingJob.description}
                        placeholder="请输入岗位描述"
                        maxLength={2000}
                        showCount
                        onChange={(e) => pageModel.updateEditingField("description", e.target.value)}
                    />
                </div>
            </Modal>
        </div>
    );
});

export default JobManagement;
